(() => {
const RECENT_STORAGE_KEY = 'etolie.recent-products.v1';
const RECENT_LEGACY_STORAGE_KEY = 'viewedProducts';
const RECENT_STORAGE_VERSION = 1;
const RECENT_HISTORY_LIMIT = 8;
const RECENT_RENDER_LIMIT = 5;
const RECENT_MINIMUM = 2;
const RECENT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

class EtolieRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.lifecycleController) return;

    this.lifecycleController = new AbortController();
    this.refreshGeneration ??= 0;
    this.sectionRequest = null;
    this.renderCache = new Map();
    this.recentGeneration ??= 0;
    this.recentRequest = null;
    this.recentObserver = null;
    this.recentIdleId = null;
    this.recentTimeoutId = null;

    this.syncHostState(this);
    this.setupRecentlyViewed();

    document.addEventListener('variant:update', this.handleVariantUpdate, {
      signal: this.lifecycleController.signal,
    });
    this.addEventListener('click', this.handleCardClick, {
      signal: this.lifecycleController.signal,
    });
  }

  // 29.09.2026 (R7): liczenie kliknięć kart w railach. Zdarzenie własne Shopify
  // (Shopify.analytics.publish) odbiera piksel klienta w panelu; URL karty zostaje czysty.
  handleCardClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const control = target?.closest('a[data-etolie-card-link], [data-etolie-atc]');
    const card = control?.closest('.etolie-card[data-product-id]');
    const rail = card?.closest('[data-etolie-rail-name]');
    if (!control || !card || !rail || !this.contains(rail)) return;

    const cards = Array.from(rail.querySelectorAll('.etolie-card[data-product-id]'));
    try {
      window.Shopify?.analytics?.publish?.('etolie:rekomendacja_klik', {
        sekcja: rail.dataset.etolieRailName,
        pozycja: cards.indexOf(card) + 1,
        akcja: control.matches('[data-etolie-atc]') ? 'koszyk' : 'otwarcie',
        produkt_id: String(card.dataset.productId || ''),
        zrodlo_produkt_id: String(this.dataset.productId || ''),
      });
    } catch (_) {
      // Analityka nie może zablokować przejścia do produktu ani koszyka.
    }
  };

  disconnectedCallback() {
    // Liczniki przeżywają ponowne podłączenie tego samego elementu. Ich
    // zwiększenie unieważnia każdy callback i finalizer zaplanowany przed
    // odpięciem, więc stary request nie może dotknąć nowej instancji UI.
    this.refreshGeneration += 1;
    this.recentGeneration += 1;
    this.lifecycleController?.abort();
    this.sectionRequest?.abort();
    this.cleanupRecentlyViewed();
    this.lifecycleController = null;
    this.sectionRequest = null;
  }

  handleVariantUpdate = (event) => {
    // Reagujemy wyłącznie na picker głównego produktu. Varianty z quick-add,
    // kart i sekcji wyróżnionych również bąbelkują do document i nie mogą
    // przebudowywać rekomendacji otwartego PDP.
    const sourcePicker = event.target instanceof Element
      ? event.target.closest('variant-picker[data-template-product-match="true"]')
      : null;
    if (!sourcePicker) return;

    const detail = event.detail || {};
    const data = detail.data || {};
    const newProduct = data.newProduct;
    const variantId = String(detail.resource?.id || '');

    // Combined listings zmieniają cały produkt. Ten przypadek musi zostać
    // obsłużony przed guardem bieżącego productId, bo event prawidłowo niesie
    // już ID nowego produktu, podczas gdy host nadal opisuje poprzedni.
    if (newProduct?.id) {
      const generation = ++this.refreshGeneration;
      const freshHost = this.findFreshHost(data.html);

      if (freshHost) {
        this.applyFreshRecommendations(freshHost, generation, newProduct);
        return;
      }

      if (variantId) {
        this.fetchFreshRecommendations(variantId, generation, newProduct);
      }
      return;
    }

    const productId = String(data.productId || '');
    const currentProductId = String(this.dataset.productId || '');

    if (productId && productId !== currentProductId) return;

    if (!variantId) return;

    const generation = ++this.refreshGeneration;
    const sectionId = String(this.dataset.sectionId || '');
    const freshFromProductResponse = data.html?.getElementById?.(`EtolieStyleGrid-${sectionId}`);

    if (freshFromProductResponse) {
      this.applyFreshStyleGrid(freshFromProductResponse, generation);
      return;
    }

    this.fetchFreshStyleGrid(variantId, generation);
  };

  findFreshHost(source) {
    const sectionId = String(this.dataset.sectionId || '');
    const hosts = source?.querySelectorAll?.('etolie-recommendations') || [];
    return Array.from(hosts).find((host) => String(host.dataset.sectionId || '') === sectionId) || null;
  }

  syncHostState(source, newProduct) {
    const freshGrid = source.querySelector?.('[data-etolie-style-grid]');
    this.dataset.productId = String(newProduct?.id || source.dataset.productId || this.dataset.productId || '');
    this.dataset.productUrl = String(newProduct?.url || source.dataset.productUrl || this.dataset.productUrl || '');
    this.dataset.currentColor = String(freshGrid?.dataset.sourceColor || '');
    this.dataset.currentVariant = String(freshGrid?.dataset.sourceVariant || '');
  }

  applyFreshRecommendations(freshHost, generation, newProduct) {
    if (generation !== this.refreshGeneration) return;
    if (!(freshHost instanceof HTMLElement)) return;

    this.setAttribute('aria-busy', 'true');
    this.cleanupRecentlyViewed();
    window.etolieDestroyCards?.(this);

    // Jedna operacja DOM podmienia cały komponent wraz z nagłówkami. Dzięki
    // temu nie może powstać stan mieszany: stylizacje nowego produktu i
    // podobne produkty poprzedniego (albo odwrotnie).
    const nodes = Array.from(freshHost.childNodes, (node) => node.cloneNode(true));
    this.replaceChildren(...nodes);
    this.syncHostState(freshHost, newProduct);
    this.querySelectorAll('[data-etolie-card-rail]').forEach((rail) => {
      rail.scrollLeft = 0;
    });

    window.etolieInitCards?.(this);
    this.setupRecentlyViewed();
    this.removeAttribute('aria-busy');
  }

  normalizeProductId(value) {
    const productId = String(value || '').trim();
    return /^\d{1,30}$/.test(productId) ? productId : '';
  }

  readRecentHistory() {
    try {
      const storedRaw = window.localStorage.getItem(RECENT_STORAGE_KEY);
      const stored = JSON.parse(storedRaw || 'null');
      let sourceEntries = [];

      this.recentLegacyPending = false;
      if (stored?.version === RECENT_STORAGE_VERSION && Array.isArray(stored.entries)) {
        sourceEntries = stored.entries;
      } else if (!storedRaw) {
        const legacy = JSON.parse(window.localStorage.getItem(RECENT_LEGACY_STORAGE_KEY) || '[]');
        if (Array.isArray(legacy)) {
          const now = Date.now();
          sourceEntries = legacy.map((id, index) => ({ id, seenAt: now - index }));
          this.recentLegacyPending = sourceEntries.length > 0;
        }
      }

      const oldestAllowed = Date.now() - RECENT_TTL_MS;
      const seen = new Set();
      const entries = [];

      sourceEntries.some((entry) => {
        const id = this.normalizeProductId(entry?.id);
        const seenAt = Number(entry?.seenAt || 0);
        if (!id || !Number.isFinite(seenAt) || seenAt < oldestAllowed || seen.has(id)) return false;

        seen.add(id);
        entries.push({ id, seenAt });
        return entries.length >= RECENT_HISTORY_LIMIT;
      });

      return entries;
    } catch (_) {
      return [];
    }
  }

  rememberCurrentProduct(currentId, history) {
    if (!currentId) return;

    const entries = [
      { id: currentId, seenAt: Date.now() },
      ...history.filter((entry) => entry.id !== currentId),
    ].slice(0, RECENT_HISTORY_LIMIT);

    try {
      window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify({
        version: RECENT_STORAGE_VERSION,
        entries,
      }));
      if (this.recentLegacyPending) {
        window.localStorage.removeItem(RECENT_LEGACY_STORAGE_KEY);
        this.recentLegacyPending = false;
      }
    } catch (_) {
      // Prywatny tryb i restrykcyjne ustawienia storage nie mogą zepsuć PDP.
    }
  }

  cleanupRecentlyViewed() {
    this.recentObserver?.disconnect();
    this.recentRequest?.abort();

    if (this.recentIdleId !== null && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.recentIdleId);
    }
    if (this.recentTimeoutId !== null) {
      window.clearTimeout(this.recentTimeoutId);
    }

    this.recentObserver = null;
    this.recentRequest = null;
    this.recentIdleId = null;
    this.recentTimeoutId = null;
  }

  setupRecentlyViewed() {
    this.cleanupRecentlyViewed();
    const generation = ++this.recentGeneration;
    const block = this.querySelector('[data-etolie-recent-block]');
    const grid = this.querySelector('[data-etolie-recent-grid]');
    const trigger = this.querySelector('[data-etolie-recent-trigger]');
    const currentId = this.normalizeProductId(this.dataset.productId);

    if (!(block instanceof HTMLElement) || !(grid instanceof HTMLElement) || !currentId) return;

    block.hidden = true;
    window.etolieDestroyCards?.(grid);
    grid.replaceChildren();

    const history = this.readRecentHistory();
    const previousIds = history
      .map((entry) => entry.id)
      .filter((id) => id !== currentId)
      .slice(0, RECENT_HISTORY_LIMIT);

    this.rememberCurrentProduct(currentId, history);

    if (previousIds.length < RECENT_MINIMUM || !(trigger instanceof HTMLElement)) {
      trigger?.remove();
      return;
    }

    const load = () => {
      if (generation !== this.recentGeneration || !this.isConnected) return;
      this.recentObserver?.disconnect();
      this.recentObserver = null;
      this.fetchRecentlyViewed(previousIds, generation);
    };

    if ('IntersectionObserver' in window) {
      this.recentObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) load();
      }, { rootMargin: '700px 0px', threshold: 0 });
      this.recentObserver.observe(trigger);
      return;
    }

    if ('requestIdleCallback' in window) {
      this.recentIdleId = window.requestIdleCallback(load, { timeout: 1200 });
      return;
    }

    this.recentTimeoutId = window.setTimeout(load, 0);
  }

  fetchRecentlyViewed(productIds, generation) {
    this.recentRequest?.abort();
    const request = new AbortController();
    this.recentRequest = request;

    const url = new URL(this.dataset.searchUrl || (window.Shopify.routes.root + 'search'), document.baseURI);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'hide');
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));

    fetch(url, {
      signal: request.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html || generation !== this.recentGeneration || !this.isConnected) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const results = documentFragment.querySelector('[data-etolie-recent-results]');
        if (!(results instanceof HTMLElement)) return;

        const cardsById = new Map();
        results.querySelectorAll('.etolie-card[data-product-id]').forEach((card) => {
          const id = this.normalizeProductId(card.dataset.productId);
          if (id && !cardsById.has(id)) cardsById.set(id, card);
        });

        const cards = productIds
          .map((id) => cardsById.get(id))
          .filter((card) => card instanceof HTMLElement)
          .slice(0, RECENT_RENDER_LIMIT);

        if (cards.length < RECENT_MINIMUM) return;

        const block = this.querySelector('[data-etolie-recent-block]');
        const grid = this.querySelector('[data-etolie-recent-grid]');
        if (!(block instanceof HTMLElement) || !(grid instanceof HTMLElement)) return;

        const nodes = cards.map((card) => card.cloneNode(true));
        grid.replaceChildren(...nodes);
        grid.scrollLeft = 0;
        window.etolieInitCards?.(grid);
        block.hidden = false;
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          // Sekcja jest opcjonalna: błąd sieci nie pozostawia pustego miejsca.
          console.warn('ETOLIE recently viewed request failed');
        }
      })
      .finally(() => {
        if (generation !== this.recentGeneration) return;
        if (this.recentRequest === request) this.recentRequest = null;
        this.querySelector('[data-etolie-recent-trigger]')?.remove();
      });
  }

  applyFreshStyleGrid(freshGrid, generation) {
    if (generation !== this.refreshGeneration) return;

    const currentGrid = this.querySelector('[data-etolie-style-grid]');
    if (!(currentGrid instanceof HTMLElement) || !(freshGrid instanceof HTMLElement)) return;

    const freshColor = String(freshGrid.dataset.sourceColor || '');
    const currentColor = String(this.dataset.currentColor || currentGrid.dataset.sourceColor || '');

    // Zmiana samego rozmiaru nie może resetować karuzeli ani kart.
    if (freshColor === currentColor) return;

    // Przy przejściowym braku propozycji zachowujemy ostatni poprawny zestaw.
    if (!freshGrid.querySelector('.etolie-card')) return;

    currentGrid.setAttribute('aria-busy', 'true');
    window.etolieDestroyCards?.(currentGrid);

    const nodes = Array.from(freshGrid.childNodes, (node) => node.cloneNode(true));
    currentGrid.replaceChildren(...nodes);
    currentGrid.dataset.sourceColor = freshColor;
    currentGrid.dataset.sourceVariant = String(freshGrid.dataset.sourceVariant || '');
    this.dataset.currentColor = freshColor;
    this.dataset.currentVariant = String(freshGrid.dataset.sourceVariant || '');
    currentGrid.scrollLeft = 0;

    window.etolieInitCards?.(currentGrid);
    currentGrid.removeAttribute('aria-busy');
  }

  fetchFreshStyleGrid(variantId, generation) {
    const sectionId = String(this.dataset.sectionId || '');
    const productUrl = String(this.dataset.productUrl || window.location.pathname);
    const productId = String(this.dataset.productId || '');
    const cacheKey = `${productId}:${variantId}`;
    const cached = this.renderCache.get(cacheKey);

    if (cached) {
      this.applyFreshStyleGrid(cached, generation);
      return;
    }

    this.sectionRequest?.abort();
    this.sectionRequest = new AbortController();

    const url = new URL(productUrl, document.baseURI);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);

    fetch(url, { signal: this.sectionRequest.signal })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html || generation !== this.refreshGeneration) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const freshGrid = documentFragment.getElementById(`EtolieStyleGrid-${sectionId}`);
        if (!(freshGrid instanceof HTMLElement)) return;
        this.renderCache.set(cacheKey, freshGrid.cloneNode(true));
        this.applyFreshStyleGrid(freshGrid, generation);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          // Ostatni poprawny rail pozostaje widoczny; błąd sieci nie psuje PDP.
          console.warn('ETOLIE recommendations refresh failed');
        }
      });
  }

  fetchFreshRecommendations(variantId, generation, newProduct) {
    const sectionId = String(this.dataset.sectionId || '');
    const productUrl = String(newProduct.url || window.location.pathname);
    const productId = String(newProduct.id || '');
    const cacheKey = `product:${productId}:${variantId}`;
    const cached = this.renderCache.get(cacheKey);

    if (cached) {
      this.applyFreshRecommendations(cached, generation, newProduct);
      return;
    }

    this.sectionRequest?.abort();
    this.sectionRequest = new AbortController();

    const url = new URL(productUrl, document.baseURI);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);

    fetch(url, { signal: this.sectionRequest.signal })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html || generation !== this.refreshGeneration) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const freshHost = this.findFreshHost(documentFragment);
        if (!(freshHost instanceof HTMLElement)) return;
        this.renderCache.set(cacheKey, freshHost.cloneNode(true));
        this.applyFreshRecommendations(freshHost, generation, newProduct);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          // Przy błędzie zachowujemy ostatni kompletny, spójny komponent.
          console.warn('ETOLIE recommendations product refresh failed');
        }
      });
  }
}

if (!customElements.get('etolie-recommendations')) {
  customElements.define('etolie-recommendations', EtolieRecommendations);
}
})();
